import type { IModule, ModuleContext } from '@punica/editor';
import { ModuleLifecycleState } from '@punica/editor';
import { MODULE_NAMES } from '@punica/editor';

/**
 * Shell ContentTabs module.
 * Depends on: kernel.commands
 */
export class ContentTabsModule implements IModule {
  readonly name = MODULE_NAMES.SHELL.CONTENT_TABS;
  readonly version = '1.0.0';
  readonly dependencies: readonly string[] = [MODULE_NAMES.KERNEL.COMMANDS];

  #state: ModuleLifecycleState = ModuleLifecycleState.CREATED;

  get state(): ModuleLifecycleState {
    return this.#state;
  }

  async bootstrap(context: ModuleContext): Promise<void> {
    const { punica, registry } = context;

    const commandsState = registry.getState(MODULE_NAMES.KERNEL.COMMANDS);
    if (commandsState !== ModuleLifecycleState.BOOTSTRAPPED) {
      throw new Error(
        'kernel.commands must be bootstrapped before shell.contentTabs'
      );
    }

    // ContentTabs is a direct API instance, not a namespace with manager
    if (!punica.shell?.ContentTabs) {
      throw new Error('ContentTabs API not available');
    }
  }

  async start(context: ModuleContext): Promise<void> {
    const { punica } = context;

    // Tabs are keyed by file path, so a rename or delete performed anywhere
    // in the app invalidates them. Rather than have every caller remember to
    // tell the shell, the substrate announces path changes and the shell
    // listens — which is why an agent invoking `file-explorer.rename` keeps
    // the tabs correct exactly like the context-menu action does.
    //
    // `start` rather than `bootstrap`: the runtime facade has to be live
    // before we can register against it.
    //
    // Both hooks are `did*`. There is no `will*` guard here on purpose — the
    // unsaved-changes warning belongs in the confirmation the caller was
    // already going to show (see the explorer's delete dialog), and raising a
    // second prompt from down here would ask the user twice.
    //
    // The returned handle is deliberately dropped: modules have no shutdown
    // lifecycle, so this registration lives as long as the process, and
    // re-registering the same id replaces rather than stacks.
    punica.runtime.fileOperations?.registerParticipant({
      id: 'shell.contentTabs',
      didRename: ({ oldPath, newPath }) => {
        punica.shell.ContentTabs.rename(oldPath, newPath);
      },
      didDelete: ({ path }) => {
        punica.shell.ContentTabs.forceClose(path);
      }
    });
  }
}
